// src/firebase/config.ts
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, getDocs, updateDoc, doc, serverTimestamp } from "firebase/firestore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// ==================== CONSTANTES PAR DÉFAUT ====================
export const DEFAULT_USER_VALUES = {
  role: "User",
  status: "Active"
};

// ==================== FONCTIONS UTILITAIRES ====================

/**
 * Met à jour tous les utilisateurs existants avec les valeurs par défaut si nécessaire
 */
export const updateAllUsersWithDefaults = async (): Promise<{ updated: number; total: number }> => {
  try {
    console.log('Début de la mise à jour des utilisateurs...');
    
    // Récupérer tous les utilisateurs
    const usersSnapshot = await getDocs(collection(db, 'users'));
    
    let updatedCount = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;
      
      // Vérifier si l'utilisateur a besoin d'être mis à jour
      const needsUpdate = !userData.role || !userData.status;
      
      if (needsUpdate) {
        const updateData: any = {};
        
        // Ajouter role par défaut si manquant
        if (!userData.role) {
          updateData.role = DEFAULT_USER_VALUES.role;
        }
        
        // Ajouter status par défaut si manquant
        if (!userData.status) {
          updateData.status = DEFAULT_USER_VALUES.status;
        }
        
        // Mettre à jour l'utilisateur
        await updateDoc(doc(db, 'users', userId), updateData);
        
        console.log(`Utilisateur ${userData.name || userData.email || userId} mis à jour:`, updateData);
        updatedCount++;
      } else {
        console.log(`Utilisateur ${userData.name || userData.email || userId} déjà à jour`);
      }
    }
    
    console.log(`Mise à jour terminée. ${updatedCount} utilisateur(s) mis à jour sur ${usersSnapshot.docs.length} total.`);
    
    return {
      updated: updatedCount,
      total: usersSnapshot.docs.length
    };
    
  } catch (error) {
    console.error('Erreur lors de la mise à jour des utilisateurs:', error);
    throw error;
  }
};

/**
 * Crée les données d'un nouvel utilisateur avec les valeurs par défaut
 */
export const createNewUserData = (userData: {
  name: string;
  email: string;
  role?: string;
  status?: string;
}) => {
  return {
    name: userData.name || '',
    email: userData.email || '',
    role: userData.role || DEFAULT_USER_VALUES.role,
    status: userData.status || DEFAULT_USER_VALUES.status,
    lastLogin: null,
    createdAt: serverTimestamp()
  };
};

/**
 * Valide et nettoie les données d'un utilisateur avant mise à jour
 */
export const validateUserData = (userData: {
  name?: string;
  email?: string;
  role?: string;
  status?: string;
}) => {
  return {
    name: userData.name || '',
    email: userData.email || '',
    role: userData.role || DEFAULT_USER_VALUES.role,
    status: userData.status || DEFAULT_USER_VALUES.status
  };
};

export { app, auth, db };
